import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import {
  fetchWalletsRaw,
  fetchTransactionsRaw,
  type AdminWalletRow,
  type AdminTransaction,
} from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

/**
 * A2.04 — Moliya dashboard'i. Mavjud `/admin/wallets` + `/admin/transactions`
 * ustidan JONLI ko'rinish: eng katta balanslar + so'nggi harakatlar. Yig'indi
 * ClickHouse/agregatsiyani KUTMAYDI — server qaytargan oyna ustidan hisoblanadi
 * (shu bois «top N» deb halol belgilangan).
 */
export function FinanceDashboard() {
  const t = useT()
  const [wallets, setWallets] = useState<AdminWalletRow[]>([])
  const [txns, setTxns] = useState<AdminTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    Promise.all([fetchWalletsRaw(ac.signal), fetchTransactionsRaw(ac.signal)])
      .then(([w, x]) => {
        if (!alive) return
        setWallets(w)
        setTxns(x)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const topWallets = wallets.slice(0, 10)
  const maxBal = topWallets.length ? topWallets[0]!.balance : 0
  const shownSum = useMemo(() => wallets.reduce((s, w) => s + w.balance, 0), [wallets])
  const recentTx = txns.slice(0, 12)

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  return (
    <>
      <PageHeader title={t.finance.dashboard} screenId="A2.04" description={t.finance.dashboardNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          <KPI label={t.finance.topWalletsSum} value={shownSum} loading={loading} accent />
          <KPI label={t.finance.walletsShown} value={wallets.length} loading={loading} />
          <KPI label={t.finance.recentTxns} value={txns.length} loading={loading} />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Panel title={t.finance.wallets}>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }, (_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            ) : !topWallets.length ? (
              <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
            ) : (
              <div className="space-y-1.5">
                {topWallets.map((w) => (
                  <div key={w.user_id} className="flex items-center gap-3">
                    <Link
                      to={`/users/${w.user_id}/wallet`}
                      className="w-40 shrink-0 truncate text-sm text-secondary hover:text-primary"
                    >
                      {w.first_name || (w.username ? `@${w.username}` : `#${w.user_id}`)}
                    </Link>
                    <div className="relative h-5 flex-1 overflow-hidden rounded bg-surface-3">
                      <div
                        className="h-full rounded bg-accent/70"
                        style={{ width: maxBal ? `${Math.max(2, (w.balance / maxBal) * 100)}%` : '0%' }}
                      />
                    </div>
                    <span className="w-20 shrink-0 text-right font-mono text-sm tabular text-primary">
                      {w.balance.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title={t.finance.recentTxns}>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }, (_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            ) : !recentTx.length ? (
              <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
            ) : (
              <div className="divide-y divide-border-subtle">
                {recentTx.map((x) => (
                  <div key={x.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                    <Link to={`/users/${x.user_id}`} className="truncate text-accent hover:underline">
                      {x.username ? `@${x.username}` : `#${x.user_id}`}
                    </Link>
                    <span className="shrink-0 text-tertiary">{x.kind}</span>
                    <span
                      className={cn(
                        'w-20 shrink-0 text-right font-mono tabular',
                        x.delta >= 0 ? 'text-success' : 'text-danger',
                      )}
                    >
                      {x.delta >= 0 ? '+' : ''}
                      {x.delta.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
  accent,
}: {
  label: string
  value: number | undefined
  loading: boolean
  accent?: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}
